import arrowLine from '@raison/static/icons/arrow-right-s-line.svg';
import {doNothing} from '@raison/toolbox';
import {VariantProps} from 'cva';
import React, {useEffect, useRef, useState} from 'react';
import {cva} from '../../Utilities';
import * as css from './RsnCarousel.css';

export interface RsnCarouselProps<T> extends VariantProps<typeof sx.container> {
    children: (slide: {key: React.Key; data: T; className: string}) => React.ReactNode;
    slides: T[];

    slide?: number;
    onSlideChange?: (slide: number) => void;
}

export function RsnCarousel<T extends object>({
    slides,
    children,
    dots,
    theme,
    gaps,
    onSlideChange: setExternalSlide = doNothing,
    slide: externalSlide = 0,
}: RsnCarouselProps<T>) {
    externalSlide = Math.min(slides.length - 1, Math.max(0, externalSlide));
    const [internalSlide, setInternalSlide] = useState(externalSlide);

    const scrollBox = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const box = scrollBox.current;
        if (!box) return;

        if (getActiveSlide(box) === externalSlide) return;

        smoothScrollAsync(box, {left: getIndexPosition(externalSlide, box)}).then(status => {
            if (status === 'canceled') setExternalSlide(getActiveSlide(box));
        });
    }, [externalSlide]);

    const handleScroll: React.UIEventHandler<HTMLDivElement> | undefined = ({currentTarget: box}) => {
        const activeSlide = getActiveSlide(box);
        const isSameSlide = internalSlide === activeSlide;
        if (isSameSlide) {
            return;
        }

        const statesAreInSync = internalSlide === externalSlide;
        if (statesAreInSync) {
            setExternalSlide(activeSlide);
        }
        setInternalSlide(activeSlide);
    };

    const isStart = internalSlide === 0;
    const isEnd = internalSlide === slides.length - 1;

    const incrementSlide = async () => {
        scrollBox.current?.scrollTo({left: getIndexPosition(internalSlide + 1, scrollBox.current), behavior: 'smooth'});
    };
    const decrementSlide = () => {
        scrollBox.current?.scrollTo({left: getIndexPosition(internalSlide - 1, scrollBox.current), behavior: 'smooth'});
    };

    return (
        <div className={sx.container({dots, theme, gaps})}>
            <div ref={scrollBox} className={sx.scrollBox} onScroll={handleScroll}>
                {slides.map((slideData, i) => children({key: i, data: slideData, className: sx.slideBox}))}
            </div>

            {slides.length > 1 && (
                <div className={sx.paginationBox}>
                    {!isStart && <img src={arrowLine} onClick={decrementSlide} className={sx.arrow.left} />}
                    {indices(slides.length).map(i => (
                        <div key={i} className={internalSlide === i ? sx.dot.current : sx.dot.other} />
                    ))}
                    {!isEnd && <img src={arrowLine} onClick={incrementSlide} className={sx.arrow.right} />}
                </div>
            )}
        </div>
    );
}

const sx = {
    ...css,
    container: cva(css.container, {
        variants: {theme: css.theme, dots: css.dots, gaps: css.gaps},
        defaultVariants: {theme: 'default', dots: 'outside', gaps: 'none'},
    }),
};

const getActiveSlide = (box: HTMLDivElement) => Math.round(box.scrollLeft / box.clientWidth);
const getIndexPosition = (index: number, box: HTMLDivElement) => index * box.clientWidth;

const indices = (length: number) =>
    Array(length)
        .fill(null)
        .map((_, i) => i);

const smoothScrollAsync = (
    element: HTMLElement,
    {
        threshold = 0.25,
        ...point
    }: {
        left?: number;
        threshold?: number;
    }
) => {
    return new Promise<'canceled' | 'arrived'>(resolve => {
        const isWithinThreshold = (a: number, b: number | undefined) =>
            Math.abs(a - (b ?? a)) < element.clientWidth * threshold;

        const isAtPoint = () => isWithinThreshold(element.scrollLeft, point.left);

        if (isAtPoint()) {
            return resolve('arrived');
        }

        let timeoutId: number;
        const scrollHandler = () => {
            window.clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => {
                if (isAtPoint()) {
                    resolve('arrived');
                } else {
                    resolve('canceled');
                }
                element.removeEventListener('scroll', scrollHandler);
            }, 50);
        };
        element.addEventListener('scroll', scrollHandler);
        element.scrollTo({...point, behavior: 'smooth'});
    });
};
